import '../styles/ViewAthletes.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  collection,
  getDocs,
  getFirestore,
  query,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import { sortAthletesByNumber } from '../helper';
import { Athlete } from '../types/types';
import AthleteTabsLoading from './AthleteTabsLoading';
import {
  AthleteCardShimmer,
  Button,
  Card,
  Container,
  Input,
  Typography,
} from './common';
import AthleteCard from './common/AthleteCard';
import NewAthleteTabs from './NewAthleteTabs';

export default function ViewAthletes() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [assignedAthletes, setAssignedAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const navigate = useNavigate();
  const db = getFirestore(firebase);
  const coachName = localStorage.getItem('coachName') || '';

  useEffect(() => {
    async function fetchAthletes() {
      if (!sessionId) {
        setError('Session ID is missing');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        
        const athletesRef = collection(db, `sessions/${sessionId}/athletes`);
        const q = query(athletesRef);
        const querySnapshot = await getDocs(q);
        
        const athletesList: Athlete[] = [];
        querySnapshot.forEach(doc => {
          athletesList.push({
            id: doc.id,
            ...doc.data()
          } as Athlete);
        });
        
        // Sort athletes by number in ascending order
        const sortedAthletes = sortAthletesByNumber(athletesList);
        setAthletes(sortedAthletes);

        const assignedList: Athlete[] = sortedAthletes.filter((a) => a.createdBy === coachName);
        setAssignedAthletes(assignedList);

      } catch (error) {
        console.error('Error fetching athletes:', error);
        setError('Failed to load athletes');
      } finally {
        setLoading(false);
      }
    }
    
    fetchAthletes();
  }, [sessionId, db]);

  const filteredAthletes = filter === 'all' ? athletes 
    : filter === 'NoCoach' ? athletes.filter(athlete => athlete.createdBy === '')
    : athletes.filter(athlete => athlete.position === filter);

  const searchFilteredAthletes = searchQuery 
    ? filteredAthletes.filter(athlete => 
        athlete.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        athlete.number.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredAthletes;

  const handleAthleteClick = (athleteId: string) => {
    navigate(`/session/${sessionId}/athlete-page/${athleteId}`);
  };

  if (!sessionId) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">Error</Typography>
          <div className="error-message">Session ID is missing</div>
          <Button 
            variant="primary" 
            fullWidth 
            onClick={() => navigate('/join')}
          >
            Join a Session
          </Button>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <Container>
          <Card className='score-athletes-card'>
            <Typography variant="h2">Score Athletes</Typography>
            
            <div className="search-bar">
              <Input
                value=""
                onChange={() => {}}
                placeholder="Search"
                className="disabled-input"
              />
            </div>
            
            <div className="filter-controls">
              <Button variant="secondary" disabled compact>All</Button>
              <Button variant="secondary" disabled compact>Tops</Button>
              <Button variant="secondary" disabled compact>Bases</Button>
              <Button variant="secondary" disabled compact>Thirds</Button>
              <Button variant="secondary" disabled compact>Multi</Button>
              <Button variant="secondary" disabled compact>Unknown</Button>
              <Button variant="secondary" disabled compact>No Coach</Button>
            </div>
            
            <div className="athletes-grid">
              {Array(6).fill(0).map((_, index) => (
                <AthleteCardShimmer key={index} />
              ))}
            </div>
            
            <div className="actions">
              <div className="shimmer shimmer-text long"></div>
              <div className="shimmer shimmer-text long"></div>
            </div>
          </Card>
        </Container>
        <AthleteTabsLoading count={4} />
      </>
    );
  }

  if (error) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">Error</Typography>
          <div className="error-message">{error}</div>
          <Button 
            variant="primary" 
            fullWidth 
            onClick={() => navigate(`/session/${sessionId}`)}
          >
            Back
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Card className='score-athletes-card'>
          <Typography variant="h2">Score Athletes</Typography>
          
          <div className="search-bar">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
            />
            {searchQuery && (
              <button 
                className="search-clear-button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="filter-controls">
            <Button 
              variant={filter === 'all' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('all')}
              compact
            >
              All
            </Button>
            <Button 
              variant={filter === 'Top' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('Top')}
              compact
            >
              Tops
            </Button>
            <Button 
              variant={filter === 'Base' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('Base')}
              compact
            >
              Bases
            </Button>
            <Button 
              variant={filter === 'Third' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('Third')}
              compact
            >
              Thirds
            </Button>
            <Button 
              variant={filter === 'Multi' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('Multi')}
              compact
            >
              Multi
            </Button>
            <Button 
              variant={filter === 'Unknown' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('Unknown')}
              compact
            >
              Unknown
            </Button>
            <Button 
              variant={filter === 'NoCoach' ? 'primary' : 'secondary'} 
              onClick={() => setFilter('NoCoach')}
              compact
            >
              No Coach
            </Button>
          </div>
          
          {searchFilteredAthletes.length === 0 ? (
            <div className="no-athletes">
              <Typography variant="h3">No athletes found</Typography>
              <Typography>Try a different search or filter</Typography>
            </div>
          ) : (
            <div className="athletes-grid">
              {searchFilteredAthletes.map(athlete => (
                <AthleteCard
                  key={athlete.id}
                  id={athlete.id}
                  name={athlete.name}
                  number={athlete.number}
                  position={athlete.position}
                  returningstatus={athlete.returningstatus}
                  coach={athlete.createdBy}
                  imageUrl={athlete.imageThumbnailUrl ?? athlete.imageUrl}
                  onClick={handleAthleteClick}
                />
              ))}
            </div>
          )}
        </Card>
      </Container>
      <NewAthleteTabs 
        athletes={assignedAthletes}
        sessionId={sessionId!}
        showHomeButton={true}
        onAthleteSelect={(athlete) => navigate(`/session/${sessionId}/athlete-page/${athlete.id}`)}
      />
    </>
  );
} 